import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box,
    CircularProgress,
    LinearProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ConfirmDialog from "../organisms/ConfirmDialog";
import ExtractDataHeader from "../organisms/ExtractDataHeader";
import { useDateValidationScan } from "../../hooks/useDateValidationScan";
import PdfPanel from "../organisms/PdfPanel";
import FormPanelContent from "../organisms/FormPanelContent";
import TaskValidator from "../organisms/TaxReturnWebforms/TaskValidator";
import DraftFoundDialog from "../molecules/DraftFoundDialog";
import BackDialog from "../molecules/BackDialog";

import { AppDispatch, RootState } from "../../configureStore";
import { ExtractTask, ReturnActive, FormExtractions } from "../../interface/returns";
import {
    getReturnTaskByKey,
    getReturnPdfByRef,
    saveExtraction,
    kickbackTask,
    RESTORE_EXTRACTED_JSON,
} from "../../redux/reducer/returns";
import { useExtractSync } from "../../hooks/useExtractSync";
import useIsMobile from "../../hooks/useIsMobile";
import { useEnsureValidToken } from "../../hooks/useEnsureValidToken";
import { saveToLocalStorage, loadFromLocalStorage, clearLocalStorage } from "../../utils/extractLocalStorage";
import { recordTaskReady } from "../../utils/taskPdfTiming";

// Configure PDF.js worker

interface ExtractDataParams {}

type LoadingPhase = "task" | "pdf" | null;

const ExtractData = ({}: ExtractDataParams) => {
    useDateValidationScan();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>("task");
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [selectedFormType, setSelectedFormType] = useState<string>("");
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [currentInstanceIndex, setCurrentInstanceIndex] = useState(0);
    const [formOnly, setFormOnly] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showBackDialog, setShowBackDialog] = useState(false);
    const [showDraftDialog, setShowDraftDialog] = useState(false);
    const [showKickbackDialog, setShowKickbackDialog] = useState(false);
    const [kickbackReason, setKickbackReason] = useState("");
    const [kickbackSubmitting, setKickbackSubmitting] = useState(false);
    const [viewAllPages, setViewAllPages] = useState(false);
    const [viewedPages, setViewedPages] = useState<Record<string, Set<number>>>({});
    const [availableFormTypes, setAvailableFormTypes] = useState<string[]>([]);
    const [formPageCounts, setFormPageCounts] = useState<Record<string, number>>({});
    const key = searchParams.get("key");
    const pdfParam = searchParams.get("pdf");
    const isMobile = useIsMobile();

    const ensureValidToken = useEnsureValidToken();
    const pdfOnly = pdfParam === "true";
    const showPdfPanel = !formOnly;
    const showFormPanel = isMobile ? formOnly : !pdfOnly;

    const activeReturnTask = useSelector<RootState, ReturnActive | undefined>(
        ({ returns }) => returns.activeReturnTask,
    );
    const activeReturnJson = useSelector<RootState, FormExtractions | undefined>(
        ({ returns }) => returns.extractedJson,
    );
    const hasChanges = useSelector<RootState, boolean>(({ returns }) => returns.hasChanges);
    const removedPageCounts = useSelector<RootState, Record<string, number>>(
        ({ returns }) =>
            availableFormTypes.reduce(
                (counts, formType) => ({
                    ...counts,
                    [formType]:
                        returns.extractedJson?.[formType as keyof FormExtractions]?.extraction?.filter(
                            (e) => e.removed === true,
                        ).length || 0,
                }),
                {} as Record<string, number>,
            ),
        shallowEqual,
    );
    const extractTask = activeReturnTask?.type === "extract" ? (activeReturnTask as ExtractTask) : undefined;

    // Structural sharing: this reference only changes when the selected form type
    // was edited, not on every field edit. Downstream memos depend on this stability.
    // (see setImmutable() in utils for more info)
    const currentFormData =
        selectedFormType && activeReturnJson ? activeReturnJson[selectedFormType as keyof FormExtractions] : null;

    // the callback is memoized because of its use as a dep in unReviewedForms
    const isFormFullyReviewed = useCallback(
        (formType: string) => {
            const totalPages = formPageCounts[formType] || 0;
            const removedCount = removedPageCounts[formType] || 0;
            const effectiveTotal = totalPages - removedCount;
            const viewedCount = viewedPages[formType]?.size || 0;
            return effectiveTotal <= 0 || viewedCount >= effectiveTotal;
        },
        [formPageCounts, removedPageCounts, viewedPages],
    );

    const unreviewedForms = useMemo(
        () => availableFormTypes.filter((f) => !isFormFullyReviewed(f)),
        [availableFormTypes, isFormFullyReviewed],
    );

    // Filter pages based on selected form type
    const filteredPages = useMemo(() => {
        if (!extractTask?.blobs?.json) return [];
        if (viewAllPages) {
            // Return all pages sorted by page number
            return [...extractTask.blobs.json]
                .filter((page) => page.isOnList)
                .sort((a, b) => a.pageNumber - b.pageNumber);
        }
        if (!selectedFormType) return [];
        return extractTask.blobs.json
            .filter(
                (page) =>
                    page.form?.type.toLowerCase().replace(/\s/g, "_").replace(/\(/g, "").replace(/\)/g, "") ===
                    selectedFormType,
            )
            .sort((a, b) => a.pageNumber - b.pageNumber);
    }, [extractTask, selectedFormType, viewAllPages]);

    // All extraction instances that share the current PDF page (multiple W-2s on one page, etc.),
    // paired with their position in the full extraction[] array so callers can mutate in place.
    const currentPageInstances = useMemo(() => {
        if (!currentFormData?.extraction || currentPageIndex >= filteredPages.length) {
            return [] as { instance: any; index: number }[];
        }
        const currentPage = filteredPages[currentPageIndex];
        return (currentFormData.extraction as any[])
            .map((instance, index) => ({ instance, index }))
            .filter(({ instance }) => instance.extractedData.originalPage === currentPage.pageNumber)
            .sort((a, b) => (a.instance.formIndexOnPage ?? 1) - (b.instance.formIndexOnPage ?? 1));
    }, [currentFormData, currentPageIndex, filteredPages]);

    const safeInstanceIndex = Math.min(currentInstanceIndex, Math.max(0, currentPageInstances.length - 1));

    const currentPageData = useMemo(() => {
        if (currentPageInstances.length === 0) return null;
        return currentPageInstances[safeInstanceIndex]?.instance ?? null;
    }, [currentPageInstances, safeInstanceIndex]);

    // Robust extraction index lookup - tries (originalPage, formIndexOnPage) first, then fallbacks
    const currentExtractionIndex = useMemo(() => {
        if (!currentFormData?.extraction || currentPageIndex >= filteredPages.length) return -1;
        const currentPage = filteredPages[currentPageIndex];
        const active = currentPageInstances[safeInstanceIndex];
        if (active) return active.index;
        // 1. Match by originalPage (first instance)
        const byOriginal = currentFormData.extraction.findIndex(
            (e) => e.extractedData.originalPage === currentPage.pageNumber,
        );
        if (byOriginal !== -1) return byOriginal;
        // 2. Match by extractedData.page
        const byPage = currentFormData.extraction.findIndex((e) => e.extractedData.page === currentPage.pageNumber);
        if (byPage !== -1) return byPage;
        // 3. Match by top-level pageNumber
        const byPageNumber = currentFormData.extraction.findIndex((e) => e.pageNumber === currentPage.pageNumber);
        if (byPageNumber !== -1) return byPageNumber;
        // 4. Fallback: use currentPageIndex if in bounds
        if (currentPageIndex < currentFormData.extraction.length) return currentPageIndex;
        return -1;
    }, [currentFormData, currentPageIndex, filteredPages, currentPageInstances, safeInstanceIndex]);

    const isCurrentPageRemoved = currentPageData
        ? currentPageData.removed === true
        : currentExtractionIndex !== -1 && currentFormData?.extraction
          ? currentFormData.extraction[currentExtractionIndex]?.removed === true
          : false;

    // Define all callbacks before any conditional returns
    const handleFormTypeChange = (event: any) => {
        setSelectedFormType(event.target.value);
        setCurrentPageIndex(0);
        setCurrentInstanceIndex(0);
        setViewAllPages(false);
    };

    const handleViewAllPages = () => {
        setViewAllPages(true);
        setSelectedFormType("");
        setCurrentPageIndex(0);
        setCurrentInstanceIndex(0);
    };

    const handlePreviousPage = () => {
        setCurrentPageIndex((prev) => Math.max(0, prev - 1));
        setCurrentInstanceIndex(0);
    };

    const handleExternalFormTypeChange = (formType: string) => {
        setSelectedFormType(formType);
        setCurrentPageIndex(0);
        setCurrentInstanceIndex(0);
    };

    const handleExternalPageIndexChange = (pageIndex: number) => {
        setCurrentPageIndex(pageIndex);
        setCurrentInstanceIndex(0);
    };

    const handleConfirmClick = () => {
        setShowConfirmDialog(true);
    };

    const handleSave = async () => {
        if (activeReturnJson && key) {
            // Ensure token is valid before saving
            await ensureValidToken();
            dispatch(
                saveExtraction({
                    input: { extractedJson: activeReturnJson, key },
                    onSuccess: () => {
                        // Clear localStorage on successful save
                        clearLocalStorage(key);
                        setShowConfirmDialog(false);
                        navigate("/returns");
                    },
                }),
            );
        }
    };

    const handleBackClick = () => {
        if (hasChanges) {
            setShowBackDialog(true);
        } else {
            navigate("/returns");
        }
    };

    const handleBackConfirm = async () => {
        if (key) {
            try {
                const token = await ensureValidToken();
                await fetch(`${import.meta.env.VITE_ENDPOINT}/returns/mark-exit`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                        "OCP-APIM-Subscription-Key": import.meta.env.VITE_OCP_APIM_SUB_KEY ?? "",
                    },
                    body: JSON.stringify({ key }),
                });
            } catch (error) {
                console.error("Failed to mark user exit:", error);
            }
        }
        setShowBackDialog(false);
        navigate("/returns");
    };

    const handleSaveAsDraft = async () => {
        if (activeReturnJson && key) {
            // Ensure token is valid before saving draft
            await ensureValidToken();
            dispatch(
                saveExtraction({
                    input: { extractedJson: activeReturnJson, key, draft: true },
                    onSuccess: () => {
                        // Clear localStorage on successful draft save
                        clearLocalStorage(key);
                        setShowBackDialog(false);
                        navigate("/returns");
                    },
                }),
            );
        }
    };

    const handleKickbackClick = () => {
        setKickbackReason("");
        setShowKickbackDialog(true);
    };

    const handleKickbackConfirm = async () => {
        if (!key || !kickbackReason.trim() || kickbackSubmitting) return;
        setKickbackSubmitting(true);
        await ensureValidToken();
        dispatch(
            kickbackTask({
                input: { key, reason: kickbackReason.trim() },
                onSuccess: () => {
                    clearLocalStorage(key);
                    setShowKickbackDialog(false);
                    setKickbackSubmitting(false);
                    navigate("/returns");
                },
                onError: () => {
                    setKickbackSubmitting(false);
                },
            }),
        );
    };

    const handleSaveDraftInPlace = async () => {
        if (activeReturnJson && key) {
            await ensureValidToken();
            dispatch(
                saveExtraction({
                    input: { extractedJson: activeReturnJson, key, draft: true },
                    onSuccess: () => {
                        clearLocalStorage(key);
                        // Stay on page — no navigate()
                    },
                }),
            );
        }
    };

    const handleAdvancePage = () => {
        setCurrentPageIndex((prev) => prev + 1);
    };

    const handleNextPage = () => {
        setCurrentPageIndex((prev) => Math.min(filteredPages.length - 1, prev + 1));
        setCurrentInstanceIndex(0);
    };

    // Set up cross-tab synchronization
    useExtractSync({
        selectedFormType,
        currentPageIndex,
        onFormTypeChange: handleExternalFormTypeChange,
        onPageIndexChange: handleExternalPageIndexChange,
    });
    // Auto-set single-panel mode on mobile
    useEffect(() => {
        if (isMobile) {
            setFormOnly(true);
        }
    }, [isMobile]);
    // Auto-save to localStorage whenever data changes
    useEffect(() => {
        if (key && activeReturnJson) {
            // Serialize Sets to arrays for storage
            const serializedViewedPages = Object.fromEntries(
                Object.entries(viewedPages).map(([k, v]) => [k, Array.from(v)]),
            );
            saveToLocalStorage(key, {
                extractedJson: activeReturnJson,
                selectedFormType,
                currentPageIndex,
                viewedPages: serializedViewedPages,
                timestamp: Date.now(),
            });
        }
    }, [activeReturnJson, selectedFormType, currentPageIndex, viewedPages, key]);
    // Load task data, restore local/draft state, and fetch PDF on mount
    useEffect(() => {
        if (!key) {
            navigate("/returns");
            return;
        }

        setLoadingPhase("task");
        dispatch(
            getReturnTaskByKey({
                input: { key },
                onSuccess: (response: { data: ExtractTask }) => {
                    const { data } = response;

                    recordTaskReady(key, {
                        taskType: data.type,
                        messageId: data.task?.message?.messageId,
                        correlationId: data.task?.message?.correlationId,
                        FICode: data.task?.message?.FICode,
                        loanNumber: data.task?.message?.loanNumber,
                    });

                    // Compute form types and page counts once at load
                    const formTypes = data.blobs?.extractedJson ? Object.keys(data.blobs.extractedJson) : [];
                    setAvailableFormTypes(formTypes);

                    const pageCounts: Record<string, number> = {};
                    formTypes.forEach((formType) => {
                        pageCounts[formType] = (data.blobs?.json ?? []).filter(
                            (page) =>
                                page.form?.type
                                    .toLowerCase()
                                    .replace(/\s/g, "_")
                                    .replace(/\(/g, "")
                                    .replace(/\)/g, "") === formType,
                        ).length;
                    });
                    setFormPageCounts(pageCounts);

                    // Try to restore from localStorage
                    const savedData = loadFromLocalStorage(key);
                    if (savedData) {
                        setSelectedFormType(savedData.selectedFormType);
                        setCurrentPageIndex(savedData.currentPageIndex);
                        // Deserialize arrays back to Sets for viewedPages
                        if (savedData.viewedPages) {
                            const loadedViewedPages: Record<string, Set<number>> = {};
                            Object.entries(savedData.viewedPages).forEach(([k, v]) => {
                                loadedViewedPages[k] = new Set(v as number[]);
                            });
                            setViewedPages(loadedViewedPages);
                        }
                        // Restore extractedJson from localStorage into Redux
                        // (backend response overwrites it, so we need to re-apply local edits)
                        if (savedData.extractedJson) {
                            dispatch({ type: RESTORE_EXTRACTED_JSON, extractedJson: savedData.extractedJson });
                        }
                    }

                    // Show draft resume dialog if a draft exists (and no local unsaved data was restored)
                    if (data.blobs?.draftExtractedJson && !savedData) {
                        setShowDraftDialog(true);
                    }

                    // Start PDF fetch
                    if (data?.blobs?.pdfRef) {
                        setLoadingPhase("pdf");
                        setDownloadProgress(0);
                        dispatch(
                            getReturnPdfByRef({
                                input: {
                                    container: data.blobs.pdfRef.container,
                                    path: data.blobs.pdfRef.path,
                                },
                                onProgress: (progress: number) => setDownloadProgress(progress),
                                onSuccess: () => {
                                    setLoadingPhase(null);
                                },
                                onError: () => {
                                    console.error("Failed to fetch PDF");
                                    setLoadingPhase(null);
                                },
                            }),
                        );
                    } else {
                        setLoadingPhase(null);
                    }
                },
                onError: () => {
                    console.error("Failed to fetch return task");
                    navigate("/returns");
                },
            }),
        );
    }, [key, dispatch, navigate]);
    // Mark current page as viewed when it changes
    useEffect(() => {
        if (selectedFormType && currentPageIndex >= 0) {
            setViewedPages((prev) => {
                const formPages = prev[selectedFormType] || new Set<number>();
                if (formPages.has(currentPageIndex)) return prev;
                const newFormPages = new Set(formPages);
                newFormPages.add(currentPageIndex);
                return { ...prev, [selectedFormType]: newFormPages };
            });
        }
    }, [selectedFormType, currentPageIndex]);

    if (loadingPhase) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Typography
                    variant="h6"
                    color="text.secondary"
                >
                    {loadingPhase === "task" ? "Loading task data..." : "Loading PDF..."}
                </Typography>
                {loadingPhase === "pdf" && downloadProgress > 0 && (
                    <Box sx={{ width: "400px", textAlign: "center" }}>
                        <LinearProgress
                            variant="determinate"
                            value={downloadProgress}
                            sx={{ mb: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {downloadProgress}% downloaded
                        </Typography>
                    </Box>
                )}
                {(loadingPhase === "task" || downloadProgress === 0) && <CircularProgress />}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: isMobile ? "100%" : "92vh",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                paddingTop: 0,
                overflow: "hidden",
            }}
        >
            <TaskValidator availableFormTypes={availableFormTypes} />
            {/* Header */}
            <ExtractDataHeader
                formOnly={formOnly}
                setFormOnly={setFormOnly}
                selectedFormType={selectedFormType}
                availableFormTypes={availableFormTypes}
                viewedPages={viewedPages}
                formPageCounts={formPageCounts}
                removedPageCounts={removedPageCounts}
                unreviewedForms={unreviewedForms}
                isFormFullyReviewed={isFormFullyReviewed}
                onFormTypeChange={handleFormTypeChange}
                onConfirmClick={handleConfirmClick}
                onBackClick={handleBackClick}
                onViewAllPages={handleViewAllPages}
                onSaveDraftInPlace={handleSaveDraftInPlace}
                onKickbackClick={handleKickbackClick}
            />

            {/* Main Content */}
            <Grid
                container
                sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}
            >
                {/* Left side - PDF Viewer */}
                {showPdfPanel && (
                    <Grid
                        size={{ xs: 12, md: isMobile || pdfOnly ? 12 : 6 }}
                        sx={{
                            borderRight: 1,
                            borderColor: "divider",
                            p: 2,
                            pb: 0.25,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            height: "100%",
                        }}
                    >
                        <PdfPanel
                            filteredPages={filteredPages}
                            currentPageIndex={currentPageIndex}
                            isCurrentPageRemoved={isCurrentPageRemoved}
                            hasCurrentPageData={!!currentPageData}
                            selectedFormType={selectedFormType}
                            viewAllPages={viewAllPages}
                            onPreviousPage={handlePreviousPage}
                            onNextPage={handleNextPage}
                            setFormOnly={setFormOnly}
                        />
                    </Grid>
                )}

                {/* Right side - Form Data Entry */}
                {showFormPanel && (
                    <Grid
                        size={{ xs: 12, md: isMobile || formOnly ? 12 : 6 }}
                        sx={{
                            p: isMobile ? 1 : 2,
                            pb: 0.25,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <FormPanelContent
                            selectedFormType={selectedFormType}
                            currentFormData={currentFormData}
                            currentPageData={currentPageData ?? null}
                            currentPageIndex={currentPageIndex}
                            currentExtractionIndex={currentExtractionIndex}
                            filteredPages={filteredPages}
                            isCurrentPageRemoved={isCurrentPageRemoved}
                            onAdvancePage={handleAdvancePage}
                            currentPageInstances={currentPageInstances}
                            currentInstanceIndex={safeInstanceIndex}
                            onInstanceIndexChange={setCurrentInstanceIndex}
                        />
                    </Grid>
                )}
            </Grid>

            {/* Confirm Dialog */}
            <ConfirmDialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={handleSave}
                unreviewedForms={unreviewedForms}
                viewedPages={viewedPages}
                formPageCounts={formPageCounts}
            />

            {/* Draft Found Dialog */}
            <DraftFoundDialog
                open={showDraftDialog}
                onClose={() => setShowDraftDialog(false)}
            />

            {/* Back Dialog */}
            <BackDialog
                open={showBackDialog}
                onClose={() => setShowBackDialog(false)}
                onSaveAsDraft={handleSaveAsDraft}
                onGoBack={handleBackConfirm}
            />

            {/* Kickback Dialog */}
            <Dialog
                open={showKickbackDialog}
                onClose={() => !kickbackSubmitting && setShowKickbackDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Send back to Identify</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This task will be moved back to the Identify queue for re-classification. Any draft
                        changes on this extract task will be discarded. A reason is required.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        label="Reason"
                        value={kickbackReason}
                        onChange={(e) => setKickbackReason(e.target.value)}
                        disabled={kickbackSubmitting}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowKickbackDialog(false)}
                        disabled={kickbackSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleKickbackConfirm}
                        disabled={!kickbackReason.trim() || kickbackSubmitting}
                    >
                        Send back
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExtractData;
